import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as VATIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import ReportDataGrid from '../components/ReportDataGrid';
import reportService from '../services/reportService';
import { useNavigate } from 'react-router-dom';
import riyalIcon from '../assets/riyal.png';
import image from '../assets/riyal.png';
import { formatDate } from '../utils/dateUtils';

const SalesSummaryReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [filters, setFilters] = useState({
    groupBy: 'day',
    customerId: '',
    startDate: new Date(),
    endDate: new Date(),
  });

  // Fetch report data
  const fetchReport = async () => {
    try {
      setLoading(true);
      const toParamDate = (date) => {
        if (!date) return undefined;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const params = {
        startDate: toParamDate(filters.startDate),
        endDate: toParamDate(filters.endDate),
      };

      if (filters.customerId) {
        params.customerId = filters.customerId;
      }

      const response = await reportService.getSalesSummary(params);

      if (response.success) {
        // Flatten the data to show individual invoices
        const flattenedData = [];
        response.data.forEach((periodGroup) => {
          if (periodGroup.invoices && periodGroup.invoices.length > 0) {
            periodGroup.invoices.forEach((invoice) => {
              flattenedData.push({
                ...invoice,
                period: periodGroup.period,
                invoiceDate: formatDate(invoice.invoiceDate),
              });
            });
          }
        });

        setReportData(flattenedData);
        setTotals(response.totals);
      }
    } catch (error) {
      console.error('Error fetching sales summary report:', error);
      // Show error notification
      alert('Failed to fetch sales summary report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate date range based on groupBy selection
  const getDateRangeForGroupBy = (groupBy) => {
    const today = new Date();
    let startDate, endDate;

    switch (groupBy) {
      case 'day':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        endDate = new Date(today);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        break;
      default:
        startDate = new Date(today);
        endDate = new Date(today);
    }

    return { startDate, endDate };
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchReport();
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    if (field === 'groupBy') {
      // When groupBy changes, automatically update the date range
      const { startDate, endDate } = getDateRangeForGroupBy(value);
      setFilters(prev => ({
        ...prev,
        [field]: value,
        startDate,
        endDate
      }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    fetchReport();
  };

  // Define columns for the data grid
  const columns = [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice No',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'invoiceDate',
      headerName: 'Date',
      flex: 0.8,
      minWidth: 110,
      sortable: true,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 1.2,
      minWidth: 180,
      sortable: true,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'subTotal',
      headerName: 'Amount',
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      field: 'vatAmount',
      headerName: 'VAT',
      flex: 0.8,
      minWidth: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      field: 'discountAmount',
      headerName: 'Discount',
      flex: 0.8,
      minWidth: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      renderCell: (params) => (
        <Box className="font-weight-bold" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      field: 'paidAmount',
      headerName: 'Paid',
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      field: 'balanceAmount',
      headerName: 'Balance',
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      renderCell: (params) => (
        <Box className={params.value > 0 ? 'text-error' : ''} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const status = params.value;
        const colors = {
          paid: { bg: '#e8f5e9', text: '#2e7d32' },
          partial: { bg: '#fff3e0', text: '#f57c00' },
          unpaid: { bg: '#ffebee', text: '#c62828' },
        };
        const color = colors[status] || colors.unpaid;

        return (
          <Chip
            label={status?.toUpperCase() || 'UNPAID'}
            size="small"
            sx={{
              bgcolor: color.bg,
              color: color.text,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '22px',
            }}
          />
        );
      },
    },
  ];

  // Summary cards data
  const summaryCards = [
    {
      title: 'Total Invoices',
      value: totals?.totalInvoices || 0,
      icon: <ReceiptIcon />,
      color: '#4361ee',
      format: (val) => val.toLocaleString(),
    },
    {
      title: 'Total Amount',
      value: totals?.grandTotal || 0,
      icon: <MoneyIcon />,
      color: '#2e7d32',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      title: 'Total VAT',
      value: totals?.totalVAT || 0,
      icon: <VATIcon />,
      color: '#ed6c02',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      title: 'Balance Due',
      value: totals?.balanceAmount || 0,
      icon: <TrendingUpIcon />,
      color: '#d32f2f',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
  ];

  const handleBack = () => {
    navigate('/reports');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {/* Header */}
      <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handleBack}
          sx={{ mr: 1.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
        >
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            Sales Summary Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View sales performance grouped by day, week, month, or year
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <Typography variant="caption" fontWeight="600" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
          Filters
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Group By"
                value={filters.groupBy}
                onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: '36px',
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 14px',
                  },
                }}
              >
                <MenuItem value="day">Daily</MenuItem>
                <MenuItem value="week">Weekly</MenuItem>
                <MenuItem value="month">Monthly</MenuItem>
                <MenuItem value="year">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        height: '36px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 14px',
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        height: '36px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 14px',
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  borderRadius: 0,
                  height: '36px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Apply Filters'}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Summary Cards */}
      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  borderLeft: `4px solid ${card.color}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      bgcolor: `${card.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { color: card.color, fontSize: 20 } })}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="h6" fontWeight="700">
                      {card.format(card.value)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Data Grid */}
      <ReportDataGrid
        title="Sales Summary Report"
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`sales-summary-${filters.groupBy}-${new Date().toISOString().split('T')[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) => row.invoiceNumber || row.id || Math.random().toString()}
        totals={totals}
        dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
      />
    </Container>
  );
};

export default SalesSummaryReport;
